import { fetchPublicConfig } from "@/shared/api/public-config";
import { isPublicWebAnalyticsAllowed } from "./analytics-consent";
import { normalizeAnalyticsRoute } from "./route-normalization";

const GOOGLE_TAG_SCRIPT_ID = "tb-public-web-google-tag";
const GOOGLE_TAG_LOAD_TIMEOUT_MS = 5_000;
const GA_COOKIE_PREFIX = /^_(?:ga(?:_|$)|gid$|gat(?:_|$)|gac_|gcl_)/i;
const MEASUREMENT_ID_PATTERN = /^G-[A-Z0-9]{4,32}$/;

type GoogleTagParams = Record<string, unknown>;
export type GoogleTagUserProperties = {
  selected_country: string;
};
type GoogleTagFunction = (...args: unknown[]) => void;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: GoogleTagFunction;
  }
}

let consentDefaultsPrepared = false;
let configuredMeasurementId = "";
let googleTagScriptLoaded = false;
let googleTagConsentGranted = false;
let initializationPromise: Promise<boolean> | null = null;

function googleTag(...args: unknown[]): void {
  if (typeof window === "undefined") return;
  if (!Array.isArray(window.dataLayer)) window.dataLayer = [];
  window.dataLayer.push(args);
}

function ensureGoogleTagQueue(): GoogleTagFunction | null {
  if (typeof window === "undefined") return null;
  if (!Array.isArray(window.dataLayer)) window.dataLayer = [];
  if (typeof window.gtag !== "function") window.gtag = googleTag;
  return window.gtag;
}

function setGoogleTagDisabled(measurementId: string, disabled: boolean): void {
  if (!measurementId || typeof window === "undefined") return;
  (window as unknown as Record<string, unknown>)[`ga-disable-${measurementId}`] = disabled;
}

export function prepareGoogleTagConsentDefaults(): void {
  if (consentDefaultsPrepared) return;
  const gtag = ensureGoogleTagQueue();
  if (!gtag) return;
  consentDefaultsPrepared = true;
  gtag("consent", "default", {
    analytics_storage: "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    wait_for_update: 500,
  });
}

function normalizeMeasurementId(value: unknown): string {
  const measurementId = String(value || "")
    .trim()
    .toUpperCase();
  return MEASUREMENT_ID_PATTERN.test(measurementId) ? measurementId : "";
}

export function sanitizedPageLocation(pathname: string, origin?: string): string {
  const pathOnly = String(pathname || "/").split(/[?#]/, 1)[0] || "/";
  const normalizedPath = normalizeAnalyticsRoute(pathOnly);
  const safePath = normalizedPath.startsWith("/") ? normalizedPath : `/${normalizedPath}`;
  const candidateOrigin = origin ?? (typeof window === "undefined" ? "" : window.location.origin);
  try {
    const safeOrigin = new URL(candidateOrigin).origin;
    return `${safeOrigin}${safePath}`;
  } catch {
    return safePath;
  }
}

export function sanitizedPageReferrer(referrer?: string): string {
  const value = referrer ?? (typeof document === "undefined" ? "" : document.referrer);
  if (!value) return "";
  try {
    const url = new URL(value);
    const currentOrigin = typeof window === "undefined" ? "" : window.location.origin;
    return url.origin === currentOrigin
      ? sanitizedPageLocation(url.pathname, url.origin)
      : url.origin;
  } catch {
    return "";
  }
}

function cookieDomainCandidates(hostname: string): string[] {
  const normalized = String(hostname || "")
    .trim()
    .replace(/^\.+/, "");
  if (!normalized || normalized === "localhost") return [];
  const labels = normalized.split(".").filter(Boolean);
  const candidates = new Set<string>([normalized, `.${normalized}`]);
  for (let index = 1; index < labels.length - 1; index += 1) {
    const suffix = labels.slice(index).join(".");
    candidates.add(suffix);
    candidates.add(`.${suffix}`);
  }
  return [...candidates];
}

export function clearGoogleAnalyticsCookies(): void {
  if (typeof document === "undefined") return;
  const cookieNames = document.cookie
    .split(";")
    .map((part) => part.split("=", 1)[0]?.trim())
    .filter((name): name is string => Boolean(name && GA_COOKIE_PREFIX.test(name)));
  const domains =
    typeof window === "undefined" ? [] : cookieDomainCandidates(window.location.hostname);
  for (const name of cookieNames) {
    document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax`;
    for (const domain of domains) {
      document.cookie = `${name}=; Max-Age=0; Path=/; Domain=${domain}; SameSite=Lax`;
    }
  }
}

function loadGoogleTagScript(measurementId: string): Promise<boolean> {
  if (typeof document === "undefined") return Promise.resolve(false);
  if (googleTagScriptLoaded) return Promise.resolve(true);

  const existing = document.getElementById(GOOGLE_TAG_SCRIPT_ID) as HTMLScriptElement | null;
  const script = existing || document.createElement("script");
  if (script.dataset.tbGoogleTagLoaded === "true") {
    googleTagScriptLoaded = true;
    return Promise.resolve(true);
  }

  return new Promise<boolean>((resolve) => {
    let settled = false;
    const settle = (loaded: boolean) => {
      googleTagScriptLoaded = loaded;
      if (settled) return;
      settled = true;
      window.clearTimeout(timeout);
      resolve(loaded);
    };
    const onLoad = () => {
      script.dataset.tbGoogleTagLoaded = "true";
      settle(true);
    };
    const onError = () => {
      if (script.id === GOOGLE_TAG_SCRIPT_ID) script.remove();
      settle(false);
    };
    const timeout = window.setTimeout(() => {
      // A blocked or stalled tag must not delay first-party measurement.
      // Keep the load listener so a late successful response can enable
      // direct collection for subsequent events.
      settled = true;
      resolve(false);
    }, GOOGLE_TAG_LOAD_TIMEOUT_MS);

    script.addEventListener("load", onLoad, { once: true });
    script.addEventListener("error", onError, { once: true });
    if (!existing) {
      script.id = GOOGLE_TAG_SCRIPT_ID;
      script.async = true;
      script.referrerPolicy = "strict-origin-when-cross-origin";
      script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(
        measurementId,
      )}`;
      document.head.appendChild(script);
    }
  });
}

async function configureGoogleTag(): Promise<boolean> {
  if (!isPublicWebAnalyticsAllowed()) return false;
  let measurementId = configuredMeasurementId;
  if (!measurementId) {
    try {
      measurementId = normalizeMeasurementId((await fetchPublicConfig()).ga4MeasurementId);
    } catch {
      return false;
    }
  }
  if (!measurementId || !isPublicWebAnalyticsAllowed()) return false;

  prepareGoogleTagConsentDefaults();
  const gtag = ensureGoogleTagQueue();
  if (!gtag) return false;

  configuredMeasurementId = measurementId;
  setGoogleTagDisabled(measurementId, false);
  googleTagConsentGranted = true;
  gtag("js", new Date());
  gtag("consent", "update", {
    analytics_storage: "granted",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });
  gtag("config", measurementId, {
    send_page_view: false,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
    page_location: sanitizedPageLocation(window.location.pathname),
    page_referrer: sanitizedPageReferrer(),
    page_title: normalizeAnalyticsRoute(window.location.pathname),
  });

  if (!isPublicWebAnalyticsAllowed()) {
    revokeGoogleTagConsent();
    return false;
  }
  return loadGoogleTagScript(measurementId);
}

export function initializeGoogleTag(): Promise<boolean> {
  if (!isPublicWebAnalyticsAllowed()) return Promise.resolve(false);
  if (configuredMeasurementId && googleTagScriptLoaded && googleTagConsentGranted) {
    return Promise.resolve(true);
  }
  if (!initializationPromise) {
    initializationPromise = configureGoogleTag();
  }
  return initializationPromise;
}

export function sendGoogleTagEvent(
  eventName: string,
  params: GoogleTagParams,
  userProperties: GoogleTagUserProperties,
): boolean {
  if (
    !isPublicWebAnalyticsAllowed() ||
    !configuredMeasurementId ||
    !googleTagScriptLoaded ||
    !googleTagConsentGranted
  ) {
    void initializeGoogleTag();
    return false;
  }
  const gtag = ensureGoogleTagQueue();
  if (!gtag) return false;
  setGoogleTagUserProperties(userProperties);
  gtag("event", eventName, {
    ...params,
    send_to: configuredMeasurementId,
  });
  return true;
}

export function setGoogleTagUserProperties(userProperties: GoogleTagUserProperties): void {
  if (!configuredMeasurementId || !googleTagConsentGranted || !isPublicWebAnalyticsAllowed()) {
    return;
  }
  ensureGoogleTagQueue()?.("set", "user_properties", userProperties);
}

export function clearGoogleTagUserProperties(): void {
  ensureGoogleTagQueue()?.("set", "user_properties", {
    selected_country: null,
  });
}

export function setGoogleTagPageLocation(pageLocation: string): void {
  if (!configuredMeasurementId || !googleTagConsentGranted || !isPublicWebAnalyticsAllowed()) {
    return;
  }
  ensureGoogleTagQueue()?.("set", {
    page_location: pageLocation,
  });
}

export function revokeGoogleTagConsent(): void {
  prepareGoogleTagConsentDefaults();
  googleTagConsentGranted = false;
  if (configuredMeasurementId) setGoogleTagDisabled(configuredMeasurementId, true);
  const gtag = ensureGoogleTagQueue();
  gtag?.("consent", "update", {
    analytics_storage: "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });
  clearGoogleTagUserProperties();
  clearGoogleAnalyticsCookies();
  initializationPromise = null;
}

if (typeof window !== "undefined") {
  // Consent defaults must precede every config/event command. This creates
  // only an in-memory queue; the external Google script is not requested
  // until initializeGoogleTag confirms analytics permission.
  prepareGoogleTagConsentDefaults();
}
